import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../contexts/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiUsers, FiPlus, FiSearch, FiBookOpen, FiClock, FiMapPin, FiStar, FiMessageCircle, FiCheck
} = FiIcons;

const StudyGroups = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { addNotification } = useApp();
  
  const [studyGroups, setStudyGroups] = useState([
    {
      id: 1,
      name: 'Mathematics Masters',
      subject: 'Mathematics',
      description: 'Advanced mathematics study group focusing on calculus and algebra. We meet weekly to solve complex problems together.',
      members: 24,
      maxMembers: 30,
      location: 'Freetown, Sierra Leone',
      meetingTime: 'Tuesdays, 4:00 PM',
      difficulty: 'Advanced',
      rating: 4.8,
      leader: {
        name: 'Aminata Kargbo',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b77c?w=100&h=100&fit=crop&crop=face'
      },
      tags: ['Calculus', 'Algebra', 'Problem Solving'],
      isJoined: false
    },
    {
      id: 2,
      name: 'Science Explorers',
      subject: 'Science',
      description: 'Explore the wonders of physics, chemistry, and biology through interactive discussions and experiments.',
      members: 18,
      maxMembers: 25,
      location: 'Banjul, The Gambia',
      meetingTime: 'Fridays, 3:00 PM',
      difficulty: 'Intermediate',
      rating: 4.6,
      leader: {
        name: 'Omar Jallow',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face'
      },
      tags: ['Physics', 'Chemistry', 'Biology'],
      isJoined: true
    },
    {
      id: 3,
      name: 'English Literature Circle',
      subject: 'English',
      description: 'Dive deep into classic and contemporary literature, improve writing skills, and engage in thoughtful discussions.',
      members: 15,
      maxMembers: 20,
      location: 'Bo, Sierra Leone',
      meetingTime: 'Wednesdays, 5:00 PM',
      difficulty: 'Beginner',
      rating: 4.9,
      leader: {
        name: 'Fatou Conteh',
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face'
      },
      tags: ['Literature', 'Writing', 'Discussion'],
      isJoined: false
    },
    {
      id: 4,
      name: 'Leadership Development',
      subject: 'Leadership',
      description: 'Develop essential leadership skills through practical exercises, case studies, and peer mentoring.',
      members: 32,
      maxMembers: 35,
      location: 'Online',
      meetingTime: 'Saturdays, 10:00 AM',
      difficulty: 'Intermediate',
      rating: 4.7,
      leader: {
        name: 'Isatu Bangura',
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face'
      },
      tags: ['Leadership', 'Communication', 'Teamwork'],
      isJoined: true
    },
    {
      id: 5,
      name: 'History Buffs',
      subject: 'History',
      description: 'Explore African history, world events, and their impact on modern society through engaging discussions.',
      members: 12,
      maxMembers: 20,
      location: 'Kenema, Sierra Leone',
      meetingTime: 'Thursdays, 6:00 PM',
      difficulty: 'Beginner',
      rating: 4.5,
      leader: {
        name: 'Lamin Jobe',
        avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face'
      },
      tags: ['African History', 'World History', 'Culture'],
      isJoined: false
    },
    {
      id: 6,
      name: 'Geography Enthusiasts',
      subject: 'Geography',
      description: 'Study physical and human geography with a focus on West African regions and global perspectives.',
      members: 8,
      maxMembers: 15,
      location: 'Serrekunda, The Gambia',
      meetingTime: 'Mondays, 4:30 PM',
      difficulty: 'Beginner',
      rating: 4.4,
      leader: {
        name: 'Mariama Sesay',
        avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face'
      },
      tags: ['Physical Geography', 'Human Geography', 'Maps'],
      isJoined: false
    }
  ]);

  const categories = [
    { id: 'all', name: 'All Groups' },
    { id: 'Mathematics', name: 'Mathematics' },
    { id: 'Science', name: 'Science' },
    { id: 'English', name: 'English' },
    { id: 'Leadership', name: 'Leadership' },
    { id: 'History', name: 'History' },
    { id: 'Geography', name: 'Geography' }
  ];

  // Handle joining/leaving a group
  const handleJoinGroup = (groupId) => {
    setStudyGroups(prevGroups => 
      prevGroups.map(group => {
        if (group.id === groupId) {
          const wasJoined = group.isJoined;
          const updatedGroup = {
            ...group,
            isJoined: !wasJoined,
            members: wasJoined ? group.members - 1 : group.members + 1
          };

          // Show notification
          addNotification({
            type: wasJoined ? 'info' : 'success',
            title: wasJoined ? 'Left Study Group' : 'Joined Study Group!',
            message: wasJoined 
              ? `You have left "${group.name}"` 
              : `Welcome to "${group.name}"! Check your messages for group details.`,
            action: wasJoined ? null : '/chat'
          });

          return updatedGroup;
        }
        return group;
      })
    );
  };

  // Handle opening group chat/messaging
  const handleMessageGroup = (group) => {
    // In a real app, this would navigate to the group's chat or create a new conversation
    addNotification({
      type: 'info',
      title: 'Opening Group Chat',
      message: `Connecting you to ${group.name} group chat...`,
      action: '/chat'
    });

    // Simulate opening chat after a brief delay
    setTimeout(() => {
      window.location.href = '/#/chat';
    }, 1500);
  };

  // Handle creating a new group
  const handleCreateGroup = () => {
    addNotification({
      type: 'info',
      title: 'Create New Group',
      message: 'Group creation feature coming soon! For now, contact an admin to create a new study group.',
    });
  };

  const filteredGroups = studyGroups.filter(group => {
    const matchesSearch = group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || group.subject === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner':
        return 'text-green-600 bg-green-100';
      case 'Intermediate':
        return 'text-yellow-600 bg-yellow-100';
      case 'Advanced':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getSubjectColor = (subject) => {
    const colors = {
      'Mathematics': 'bg-blue-500',
      'Science': 'bg-green-500',
      'English': 'bg-purple-500',
      'Leadership': 'bg-yellow-500',
      'History': 'bg-orange-500',
      'Geography': 'bg-teal-500'
    };
    return colors[subject] || 'bg-gray-500';
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-8 border border-gray-100"
      >
        <div className="text-center">
          <SafeIcon icon={FiUsers} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Study Groups</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Join study groups to collaborate with fellow students, share knowledge, and achieve your academic goals together. Learning is more fun and effective when done with others!
          </p>
        </div>
      </motion.div>

      {/* Search and Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search groups by name, description, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-primary-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Create Group Button */}
          <button 
            onClick={handleCreateGroup}
            className="flex items-center space-x-2 bg-accent-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-accent-600 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
            <span>Create Group</span>
          </button>
        </div>
      </motion.div>

      {/* Groups Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredGroups.map((group, index) => (
          <motion.div
            key={group.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden"
          >
            {/* Header */}
            <div className={`${getSubjectColor(group.subject)} p-6 text-white relative overflow-hidden`}>
              <div className="absolute top-0 right-0 w-20 h-20 bg-white bg-opacity-10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-xl font-bold mb-1">{group.name}</h3>
                    <p className="text-sm opacity-90">{group.subject}</p>
                  </div>
                  <div className="flex items-center space-x-1 bg-white bg-opacity-20 px-2 py-1 rounded-full">
                    <SafeIcon icon={FiStar} className="w-4 h-4" />
                    <span className="text-sm font-medium">{group.rating}</span>
                  </div>
                </div>
                <p className="text-sm opacity-75 line-clamp-2">{group.description}</p>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Leader */}
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src={group.leader.avatar}
                  alt={group.leader.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium text-gray-800">{group.leader.name}</p>
                  <p className="text-sm text-gray-500">Group Leader</p>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiUsers} className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Members</span>
                  </div>
                  <span className="text-sm font-medium text-gray-800">
                    {group.members}/{group.maxMembers}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiClock} className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Meeting Time</span>
                  </div>
                  <span className="text-sm font-medium text-gray-800">{group.meetingTime}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiMapPin} className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Location</span>
                  </div>
                  <span className="text-sm font-medium text-gray-800">{group.location}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Difficulty</span>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${getDifficultyColor(group.difficulty)}`}>
                    {group.difficulty}
                  </span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Group Capacity</span>
                  <span>{Math.round((group.members / group.maxMembers) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-primary-500 to-accent-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(group.members / group.maxMembers) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Tags */}
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  {group.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                {group.isJoined ? (
                  <>
                    <button
                      onClick={() => handleJoinGroup(group.id)}
                      className="flex-1 bg-green-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      <span>Joined</span>
                    </button>
                    <button
                      onClick={() => handleMessageGroup(group)}
                      className="flex items-center justify-center bg-blue-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                      title="Open group chat"
                    >
                      <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleJoinGroup(group.id)}
                    disabled={group.members >= group.maxMembers}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                      group.members >= group.maxMembers
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-primary-500 to-accent-500 text-white hover:from-primary-600 hover:to-accent-600'
                    }`}
                  >
                    {group.members >= group.maxMembers ? 'Group Full' : 'Join Group'}
                  </button>
                )}
              </div>

              {/* Join Status Info */}
              {group.isJoined && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 inline mr-1" />
                    You're a member of this group! Click the message button to join the group chat.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* No Results */}
      {filteredGroups.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <SafeIcon icon={FiSearch} className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No groups found</h3>
          <p className="text-gray-600 mb-6">
            Try adjusting your search terms or create a new study group to get started!
          </p>
          <button 
            onClick={handleCreateGroup}
            className="bg-primary-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-600 transition-colors"
          >
            Create New Group
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default StudyGroups;